import { apiClient } from "@/lib/apiClient";
import { getAuthRegistry } from "@/lib/auth/AuthRegistry";
import { noteDashboardHandoff } from "@/gtm.client";

let dashboardTabRef: Window | null = null;

/**
 * Gets the dashboard base URL from the stored region.
 * - region "by" -> https://dash-by.boona.app
 * - region "de" -> https://dash-de.boona.app
 * - no region (fallback) -> https://dash.boona.app
 */
function getDashboardBase(): string {
  const region = getAuthRegistry().getRegion();
  return region ? `https://dash-${region}.boona.app` : "https://dash.boona.app";
}

/**
 * Navigates to the FastAPI dashboard in a new tab, handling popup blockers
 * by opening the window immediately during user interaction.
 *
 * @param path - Dashboard path (e.g., "/dashboard/records")
 * @throws Error if popup is blocked or API call fails
 */
export async function goToDashboard(path: string = "/dashboard") {
  // Tell the demo instrumentation this tab is about to be backgrounded on purpose,
  // so the resulting demo_exit reads as a dashboard hand-off and not a departure.
  // No-ops outside demo sessions.
  noteDashboardHandoff();

  const backendBase = getDashboardBase();

  // Normalize the path
  let normalizedPath = (path || "").trim();
  if (!normalizedPath) normalizedPath = "/dashboard";
  if (!normalizedPath.startsWith("/")) normalizedPath = `/${normalizedPath}`;
  if (!/^\/dashboard(\/|$)/.test(normalizedPath)) {
    normalizedPath = `/dashboard${normalizedPath}`;
  }
  normalizedPath = normalizedPath.replace(/\/{2,}/g, "/");

  const finalUrl = `${backendBase}${normalizedPath}`;

  // Close existing dashboard tab if it's still open
  if (dashboardTabRef && !dashboardTabRef.closed) {
    try {
      dashboardTabRef.close();
    } catch (_) {
      // Ignore errors when closing
    }
    dashboardTabRef = null;
  }

  // CRITICAL: Open window IMMEDIATELY before any async operations
  // This keeps the window.open() in the same execution context as the user interaction
  const loadingHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Loading Dashboard...</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            display: flex; align-items: center; justify-content: center;
            min-height: 100vh;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .loader-container { text-align: center; }
          .spinner {
            width: 50px; height: 50px; margin: 0 auto 20px;
            border: 4px solid rgba(255, 255, 255, 0.3);
            border-top: 4px solid white; border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          h2 { font-size: 24px; font-weight: 600; margin-bottom: 8px; }
          p { font-size: 14px; opacity: 0.9; }
        </style>
      </head>
      <body>
        <div class="loader-container">
          <div class="spinner"></div>
          <h2>Loading Dashboard</h2>
          <p>Please wait...</p>
        </div>
      </body>
    </html>
  `;

  const newWin = window.open("about:blank", "_blank");
  dashboardTabRef = newWin;

  if (!newWin) {
    dashboardTabRef = null;
    throw new Error("Unable to open dashboard tab. Please check your popup blocker settings.");
  }

  try {
    newWin.document.write(loadingHTML);
    newWin.document.close();
  } catch (_) {
    // If writing fails, continue anyway
  }

  try {
    // Make API call to set SSO cookie
    await apiClient.post("/api/v1/auth/sso/cookie", null, {
      withCredentials: true
    });

    // Navigate to the final dashboard URL
    newWin.location.href = finalUrl;
  } catch (err) {
    try {
      newWin.close();
    } catch (_) { }
    dashboardTabRef = null;
    throw err;
  }
}

/**
 * Closes the dashboard tab if it's currently open
 */
export function closeDashboard(): void {
  if (dashboardTabRef && !dashboardTabRef.closed) {
    try {
      dashboardTabRef.close();
    } catch (_) { }
    dashboardTabRef = null;
  }
}

/**
 * Checks if the dashboard tab is currently open
 */
export function isDashboardOpen(): boolean {
  return dashboardTabRef !== null && !dashboardTabRef.closed;
}